/**
 * The single RTK Query API. Features add endpoints with `baseApi.injectEndpoints(...)`.
 *
 * Every request carries the in-memory access token. On a 401 the client refreshes the
 * session ONCE (a mutex stops ten parallel 401s from refreshing ten times), retries the
 * request, and signs the user out if the refresh fails (plan §9.1).
 *
 * Responses arrive in the envelope from `./response`: queries (GET) receive its `data`,
 * mutations receive the whole write envelope (`ApiMessage<T>`) so they can show `message`.
 */
import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';

import { env } from '@shared/config/env';
import { isEnvelope, type ApiMessage } from '@shared/api/response';
import type { SessionResponse } from '@shared/api/types';
import { sessionReceived, signedOut, type SessionState } from '@shared/session/sessionSlice';
import { REGEX } from '@shared/constants/regex';

/** Relative bases (`/api/v1`, via the dev proxy) resolve against the page origin;
 *  absolute ones (a separate API host in production) are used as-is. */
export const apiBaseUrl = new URL(env.apiBaseUrl, window.location.origin)
  .toString()
  .replace(REGEX.trailingSlash, '');

/** Emails read in upper case on screen like every other value, but are always sent in lower
 *  case: any `email` field in a JSON body, at any depth. Files and form data pass untouched. */
export function lowercaseEmails(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(lowercaseEmails);
  if (value === null || typeof value !== 'object' || Object.getPrototypeOf(value) !== Object.prototype) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value).map(([k, v]) => [
      k,
      k === 'email' && typeof v === 'string' ? v.toLowerCase() : lowercaseEmails(v),
    ]),
  );
}

const fetchQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  credentials: 'include', // refresh-token cookie
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as { session: SessionState }).session.accessToken;
    if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    return headers;
  },
});

export const rawBaseQuery: typeof fetchQuery = (args, api, extraOptions) =>
  fetchQuery(
    typeof args === 'object' && args.body !== undefined
      ? { ...args, body: lowercaseEmails(args.body) }
      : args,
    api,
    extraOptions,
  );

const refreshMutex = new Mutex();

/** Endpoints whose 401 means "wrong credentials / no session", not "token expired". */
const NO_REAUTH = ['/auth/login', '/auth/refresh', '/auth/logout'];

function urlOf(args: string | FetchArgs): string {
  return typeof args === 'string' ? args : args.url;
}

function isRead(args: string | FetchArgs): boolean {
  return typeof args === 'string' || (args.method ?? 'GET').toUpperCase() === 'GET';
}

/** Reads get the envelope's `data`; writes keep the envelope. Non-JSON bodies pass through. */
function unwrap<R extends { data?: unknown }>(args: string | FetchArgs, result: R): R {
  if (!isRead(args) || !isEnvelope(result.data) || !('data' in result.data)) return result;
  return { ...result, data: result.data.data };
}

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  await refreshMutex.waitForUnlock();
  let result = await rawBaseQuery(args, api, extraOptions);
  if (result.error?.status !== 401 || NO_REAUTH.some((p) => urlOf(args).startsWith(p))) {
    return unwrap(args, result);
  }

  if (refreshMutex.isLocked()) {
    await refreshMutex.waitForUnlock();
  } else {
    const release = await refreshMutex.acquire();
    try {
      const refreshed = await rawBaseQuery({ url: '/auth/refresh', method: 'POST' }, api, extraOptions);
      if (refreshed.data) {
        api.dispatch(sessionReceived((refreshed.data as ApiMessage<SessionResponse>).data));
      } else {
        api.dispatch(signedOut());
        return result;
      }
    } finally {
      release();
    }
  }
  result = await rawBaseQuery(args, api, extraOptions);
  return unwrap(args, result);
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Me',
    'User',
    'UserOption',
    'Audit',
    'Service',
    'Year',
    'DueRules',
    'Client',
    'ClientGroup',
    'Task',
    'RoleDefaults',
    'Owner',
    'Profile',
    'Register',
    'Tracker',
    'BkLookup',
    'ClientView',
    'ClientYear',
    'Invoice',
    'BillingSettings',
    'PriceList',
    'BillingAnalytics',
    'Notification',
    'InboxSummary',
    'ChatPartners',
    'ChatThread',
    'ChatSaved',
    'Query',
    'Letter',
    'LetterTemplate',
    'LetterSettings',
    'Storage',
    'Vault',
    'Preference',
  ],
  endpoints: () => ({}),
});
